import React, { useEffect, useState } from 'react';
import { filter, take, Subscription } from 'rxjs';
import { removeAuthCookie, DEFAULT_IB_AUTH_COOKIE_NAME } from 'web-lib-application-essentials/lib/cookie';
import { getAuthDomain } from 'web-lib-application-essentials/lib/urlUtil';

import { SystemErrors } from '../../system-utils/system-types';
import { bootstrapHostApp } from './bootstrap-host-app';
import * as EventsManager from '../../system-utils/events-manager';
import { isNativeView } from '../../vm-omega-shared-component-webapp';
import { getSessionManager } from '../../authorisation-utils/session-manager';
import * as InteractionHandler from '../../system-utils/interaction-handler';
import ErrorPage from '../ErrorPage';
import InvalidToken from '../InvalidToken';
import SessionExpired from '../SessionExpired';
import LoadingView from '../LoadingView';
import { DynamicPageTitle } from './DynamicPageTitle';
import { APIResponse } from '../../system-libraries/api-types';
import { deleteAccessToken } from '../../system-utils/api-utils/endpoints/delete-access-token';

import { DeleteAccessTokenApiResponseBody } from '../../system-utils/api-utils/endpoints/types';
import { longTermToken } from '../../system-utils/events-manager';

export const Session: React.FC = ({ children }) => {
  const [systemError, setSystemError] = useState(EventsManager.systemError.getSystemError());
  const [timeout, setTimeout] = useState(EventsManager.sessionTimeoutWarning.value.timeoutIn);
  const [showLoadingView, setShowLoadingView] = useState(EventsManager.loadingView.isShowing());

  const bootstrap = () => {
    EventsManager.longTermToken.subject
      .pipe(
        filter(({ isValid }) => isValid),
        take(1),
      )
      .subscribe(() => {
        const session = getSessionManager();
        session.startTimer();
      });
    bootstrapHostApp();
  };

  const subscribeToSystemErrors = (): Subscription => {
    return EventsManager.systemError.subject.subscribe(error => setSystemError(error));
  };

  const subscribeToSessionTimeouts = (): Subscription => {
    return EventsManager.sessionTimeoutWarning.subscribe(sess => setTimeout(sess.timeoutIn));
  };

  const subscribeToLoadingViewEvents = (): Subscription => {
    return EventsManager.loadingView.subject.subscribe(({ show }) => setShowLoadingView(show));
  };

  // Triggered before a refresh or tab close.
  const onWindowBeforeUnload = async () => {
    // need to check if token is to be removed
    // cannot be removed if redirecting to bib
    if (EventsManager.longTermToken.hasToken() && EventsManager.longTermToken.isTokenDelete()) {
      makeDeleteAccessTokenCall();
      EventsManager.longTermToken.clearToken();
      if (process.env.NODE_ENV === 'production') {
        removeAuthCookie(DEFAULT_IB_AUTH_COOKIE_NAME, getAuthDomain());
      }
    }
  };

  const makeDeleteAccessTokenCall = async (): Promise<APIResponse<DeleteAccessTokenApiResponseBody>> => {
    try {
      const accessToken = longTermToken.getToken();
      await deleteAccessToken(accessToken);
    } catch (error) {
      return {
        error,
      };
    }
  };

  useEffect(() => {
    bootstrap();
    const systemErrorSub = subscribeToSystemErrors();
    const sessionTimeoutWarningSub = subscribeToSessionTimeouts();
    const loadingViewSub = subscribeToLoadingViewEvents();
    InteractionHandler.addEventListeners();
    window.addEventListener('beforeunload', onWindowBeforeUnload);

    return () => {
      systemErrorSub.unsubscribe();
      sessionTimeoutWarningSub.unsubscribe();
      loadingViewSub.unsubscribe();
      InteractionHandler.removeEventListeners();
      window.removeEventListener('beforeunload', onWindowBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (timeout === 0) {
      // Invalidate long term token, forcing user to login since the token will also be removed
      // from the session storage so a page refresh cannot reset the session.
      makeDeleteAccessTokenCall();
      EventsManager.longTermToken.clearToken();
      if (process.env.NODE_ENV === 'production') {
        removeAuthCookie(DEFAULT_IB_AUTH_COOKIE_NAME, getAuthDomain());
      }
    }
  }, [timeout]);

  const renderApp = () => {
    if (timeout === 0) {
      return <SessionExpired />;
    }

    if (systemError.type === SystemErrors.INVALID_SHORT_TERM_TOKEN) {
      return <InvalidToken />;
    }

    if (systemError.type === SystemErrors.CRITICAL_ERROR) {
      // TODO: check if session timeout warning pop up can display over this view?
      return <ErrorPage description={systemError.data.description} isNativeView={isNativeView()} />;
    }

    // Session has not started
    if (timeout === -1) {
      return null;
    }

    return children;
  };

  return (
    <>
      <DynamicPageTitle />
      {renderApp()}
      {showLoadingView && <LoadingView />}
    </>
  );
};
